import { ComponentProps, useCallback, useId, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  MessageIntent,
  SubBrand,
  Triggered_Message_Status,
  TriggeredMessageViewFragment,
} from '@occc/graphql';
import {
  EngagementChannelConfigForm,
  EngagementChannelConfigFormValues,
  getFormValues,
  getUpdateChannelsConfigInput,
} from '@occc/ui-notifications';
import { snackbar } from '@occc-app/design-system';

import { useUpdateTriggeredMessage } from './use-update-triggered-message.hook';

import { MESSAGE_UPDATE_SUCCESS } from '../triggered-messages.constants';
import { routeCreator } from '../triggered-messages.utils';

export type TriggeredMessageEditFormValues = EngagementChannelConfigFormValues & {
  messageIntent: MessageIntent | null;
};

export const useEngagementChannelConfigForm = (
  message: TriggeredMessageViewFragment,
  subBrand?: SubBrand,
) => {
  const initialValues: TriggeredMessageEditFormValues = useMemo(() => {
    return {
      ...getFormValues(message.channelsConfig),
      messageIntent: message.messageIntent,
    };
  }, [message]);
  const isReadOnly = message.status === Triggered_Message_Status.Disabled;

  const navigate = useNavigate();
  const goToMessageView = useCallback(() => {
    const to = subBrand
      ? routeCreator.viewScoped.to(subBrand.uuid, message.uuid)
      : routeCreator.view.to(message.uuid);

    navigate(to);
  }, [message.uuid, navigate, subBrand]);

  const [update, { loading: isBusy }] = useUpdateTriggeredMessage({
    onCompleted: () => {
      snackbar.success(MESSAGE_UPDATE_SUCCESS);
      goToMessageView();
    },
  });
  const id = useId();

  const onSubmit: ComponentProps<typeof EngagementChannelConfigForm>['onSubmit'] = useCallback(
    async (values) => {
      const formValues = values as TriggeredMessageEditFormValues;
      const channelsConfig = getUpdateChannelsConfigInput(formValues);

      await update({
        variables: {
          input: {
            triggeredMessageUuid: message.uuid,
            channelsConfig,
            messageIntent: formValues.messageIntent,
          },
        },
        context: {
          messageIntent: formValues.messageIntent,
        },
      });
    },
    [update, message],
  );

  return {
    initialValues,
    isReadOnly,
    isBusy,
    onSubmit,
    onCancel: goToMessageView,
    id,
  };
};
